import React, { useState, useEffect, useCallback } from 'react';
import icebreakerService, {
  IcebreakerChannel,
  EnrichmentSource,
  IcebreakerVariant,
  EnrichmentResult,
} from '../services/icebreakerService';
import gtmService, { Icp, Persona, Offer } from '../services/gtmService';
import MarkdownView from '../components/MarkdownView';
import SavedAiDocuments from '../components/SavedAiDocuments';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';

const ALL_SOURCES: { value: EnrichmentSource; label: string; hint: string }[] = [
  { value: 'research', label: 'Web-Research (Perplexity)', hint: 'Firmen-/Branchen-Kontext, NIS2-/Compliance-Trigger — automatisch' },
  { value: 'website', label: 'Website-Scraping', hint: 'Firmen-Website auslesen (Leistungen, Region, Hinweise)' },
  { value: 'signal', label: 'LinkedIn-Post (Signal)', hint: 'Letzten Post als Aufhänger einfügen' },
  { value: 'manual', label: 'Manueller Kontext', hint: 'Eigene Notizen / Infos ergänzen' },
];

const IcebreakerPage: React.FC = () => {
  // Kontakt
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [website, setWebsite] = useState('');
  const [linkedInUrl, setLinkedInUrl] = useState('');

  // Quellen
  const [sources, setSources] = useState<EnrichmentSource[]>(['research', 'website', 'signal']);
  const [signalText, setSignalText] = useState('');
  const [manualContext, setManualContext] = useState('');

  // Optionen
  const [channels, setChannels] = useState<IcebreakerChannel[]>(['email', 'linkedin']);
  const [variantCount, setVariantCount] = useState(2);
  const [tone, setTone] = useState('warm, professionell, auf Augenhöhe');
  const [language, setLanguage] = useState('de');
  const [goal, setGoal] = useState('Ein kurzes, unverbindliches Gespräch vereinbaren.');

  // GTM
  const [icps, setIcps] = useState<Icp[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [icpId, setIcpId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [offerId, setOfferId] = useState('');

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [variants, setVariants] = useState<IcebreakerVariant[]>([]);
  const [contextUsed, setContextUsed] = useState<string[]>([]);
  const [enrichment, setEnrichment] = useState<EnrichmentResult | null>(null);
  const [copied, setCopied] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const loadGtm = useCallback(async () => {
    try {
      const [i, p, o] = await Promise.all([
        gtmService.getIcps(),
        gtmService.getPersonas(),
        gtmService.getOffers(),
      ]);
      setIcps(i);
      setPersonas(p);
      setOffers(o);
    } catch {
      // optional
    }
  }, []);

  useEffect(() => {
    loadGtm();
  }, [loadGtm]);

  // Beim Wechsel auf eine ICP deren gespeicherte Icebreaker-Defaults übernehmen.
  const handleIcpChange = (id: string) => {
    setIcpId(id);
    const icp = icps.find((i) => i.id === id);
    if (!icp) return;
    const splitCsv = (v?: string) =>
      (v || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    if (icp.icebreakerTone) setTone(icp.icebreakerTone);
    if (icp.icebreakerLanguage) setLanguage(icp.icebreakerLanguage);
    if (icp.icebreakerGoal) setGoal(icp.icebreakerGoal);
    const channelDefaults = splitCsv(icp.icebreakerChannels) as IcebreakerChannel[];
    if (channelDefaults.length) setChannels(channelDefaults);
    const sourceDefaults = splitCsv(icp.icebreakerAutoSources) as EnrichmentSource[];
    if (sourceDefaults.length) setSources(sourceDefaults);
  };

  const toggle = <T,>(list: T[], value: T, setter: (v: T[]) => void) => {
    setter(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  };

  const buildContact = () => ({
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    jobTitle: jobTitle || undefined,
    company: company || undefined,
    website: website || undefined,
    linkedInUrl: linkedInUrl || undefined,
  });

  const copy = (key: string, text: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const handleRun = async () => {
    if (!company && !firstName && !lastName) {
      setError('Bitte mindestens Name oder Firma angeben.');
      return;
    }
    if (!channels.length) {
      setError('Bitte mindestens einen Kanal wählen.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setVariants([]);
      setEnrichment(null);

      // Auto-Quellen, die der Server aktiv abruft (research, website, LinkedIn-Post)
      const autoSources = sources.filter(
        (s) => s === 'research' || s === 'website' || s === 'signal'
      );

      const result = await icebreakerService.run({
        contact: buildContact(),
        channels,
        variantCount,
        tone,
        language,
        goal: goal || undefined,
        signalText: sources.includes('signal') ? signalText || undefined : undefined,
        manualContext: sources.includes('manual') ? manualContext || undefined : undefined,
        autoSources,
        icpId: icpId || undefined,
        personaId: personaId || undefined,
        offerId: offerId || undefined,
      });

      setVariants(result.variants);
      setContextUsed(result.contextUsed);
      setEnrichment(result.enrichment);
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der Icebreaker-Generierung');
    } finally {
      setLoading(false);
    }
  };

  const emailVariants = variants.filter((v) => v.channel === 'email');
  const linkedinVariants = variants.filter((v) => v.channel === 'linkedin');

  return (
    <div className="page-container">
      <PageHeader title="Einstiegssätze (Icebreaker)" subtitle={routeById('icebreaker')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Personalisierte Einleitungen für E-Mail &amp; LinkedIn — ohne Clay. Der Kontakt wird über
        Web-Research, Website und/oder LinkedIn-Post selbst angereichert und auf dein GTM-Setup
        (ICP/Persona/Angebot) geerdet.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Kontakt */}
      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
        <h3 style={{ marginTop: 0 }}>1. Kontakt</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 160px' }}>
            <label className="form-label">Vorname</label>
            <input className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: '1 1 160px' }}>
            <label className="form-label">Nachname</label>
            <input className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">Rolle / Titel</label>
            <input className="form-input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="z.B. IT-Leiter" />
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">Unternehmen</label>
            <input className="form-input" value={company} onChange={(e) => setCompany(e.target.value)} />
          </div>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">Website</label>
            <input className="form-input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="firma.de" />
          </div>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">LinkedIn-URL</label>
            <input className="form-input" value={linkedInUrl} onChange={(e) => setLinkedInUrl(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Quellen */}
      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-success)' }}>
        <h3 style={{ marginTop: 0 }}>2. Personalisierungs-Quellen (statt Clay)</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px' }}>
          {ALL_SOURCES.map((s) => (
            <label key={s.value} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flex: '1 1 220px', cursor: 'pointer' }}>
              <input type="checkbox" checked={sources.includes(s.value)} onChange={() => toggle(sources, s.value, setSources)} style={{ marginTop: '3px' }} />
              <span>
                <strong>{s.label}</strong>
                <br />
                <span style={{ color: 'var(--sec-fg-muted)', fontSize: '12px' }}>{s.hint}</span>
              </span>
            </label>
          ))}
        </div>
        {sources.includes('signal') && (
          <div className="form-group">
            <label className="form-label">LinkedIn-Post / Signal (optional einfügen)</label>
            <textarea className="form-textarea" rows={4} value={signalText} onChange={(e) => setSignalText(e.target.value)} placeholder="Leer lassen für automatischen Abruf des letzten Posts – oder Post hier einfügen…" />
            <small style={{ color: 'var(--sec-fg-muted)' }}>
              Wird das Feld leer gelassen, ruft das Tool den letzten Post automatisch über deine
              LinkedIn-Verbindung ab (URL mit Personen-URN nötig). Eingefügter Text hat Vorrang.
            </small>
          </div>
        )}
        {sources.includes('manual') && (
          <div className="form-group">
            <label className="form-label">Manueller Kontext</label>
            <textarea className="form-textarea" rows={3} value={manualContext} onChange={(e) => setManualContext(e.target.value)} placeholder="Eigene Infos: Auslöser, gemeinsame Kontakte, Event, Ausschreibung…" />
          </div>
        )}
      </div>

      {/* Optionen + GTM */}
      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-warning)' }}>
        <h3 style={{ marginTop: 0 }}>3. Ausgabe &amp; GTM-Erdung</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '12px' }}>
          <div>
            <label className="form-label">Kanäle</label>
            <div style={{ display: 'flex', gap: '14px' }}>
              <label style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={channels.includes('email')} onChange={() => toggle(channels, 'email', setChannels)} /> E-Mail
              </label>
              <label style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={channels.includes('linkedin')} onChange={() => toggle(channels, 'linkedin', setChannels)} /> LinkedIn
              </label>
            </div>
          </div>
          <div className="form-group" style={{ width: '120px' }}>
            <label className="form-label">Varianten</label>
            <select className="form-input" value={variantCount} onChange={(e) => setVariantCount(Number(e.target.value))}>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </select>
          </div>
          <div className="form-group" style={{ width: '120px' }}>
            <label className="form-label">Sprache</label>
            <select className="form-input" value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="de">Deutsch</option>
              <option value="en">Englisch</option>
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">Tonalität</label>
            <input className="form-input" value={tone} onChange={(e) => setTone(e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Ziel / CTA</label>
          <input className="form-input" value={goal} onChange={(e) => setGoal(e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 180px' }}>
            <label className="form-label">Angebot (GTM, optional)</label>
            <select className="form-input" value={offerId} onChange={(e) => setOfferId(e.target.value)}>
              <option value="">— keins —</option>
              {offers.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 180px' }}>
            <label className="form-label">ICP (GTM, optional)</label>
            <select className="form-input" value={icpId} onChange={(e) => handleIcpChange(e.target.value)}>
              <option value="">— keiner —</option>
              {icps.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                  {(i.icebreakerTone ||
                    i.icebreakerLanguage ||
                    i.icebreakerGoal ||
                    i.icebreakerChannels ||
                    i.icebreakerAutoSources)
                    ? ' ✓'
                    : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 180px' }}>
            <label className="form-label">Persona (GTM, optional)</label>
            <select className="form-input" value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
              <option value="">— keine —</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        <button className="btn btn-success" onClick={handleRun} disabled={loading}>
          {loading ? 'Generiere…' : 'Icebreaker erstellen'}
        </button>
      </div>

      {/* Anreicherungs-Hinweise */}
      {enrichment && (enrichment.warnings.length > 0 || enrichment.sourcesUsed.length > 0) && (
        <div className="card" style={{ marginTop: '16px' }}>
          <div className="card-title">Anreicherung</div>
          {enrichment.sourcesUsed.length > 0 && (
            <p style={{ color: 'var(--sec-success)', fontSize: '13px', margin: '8px 0' }}>
              Genutzte Auto-Quellen: {enrichment.sourcesUsed.join(', ')}
            </p>
          )}
          {enrichment.warnings.map((w, i) => (
            <p key={i} style={{ color: 'var(--sec-warning)', fontSize: '13px', margin: '4px 0' }}>⚠ {w}</p>
          ))}
          {enrichment.research && (
            <details style={{ marginTop: '8px' }}>
              <summary style={{ cursor: 'pointer' }}>Web-Research ansehen</summary>
              <MarkdownView content={enrichment.research} />
            </details>
          )}
          {enrichment.websiteSummary && (
            <details style={{ marginTop: '8px' }}>
              <summary style={{ cursor: 'pointer' }}>Website-Zusammenfassung ansehen</summary>
              <MarkdownView content={enrichment.websiteSummary} />
            </details>
          )}
          {enrichment.signalText && (
            <details style={{ marginTop: '8px' }}>
              <summary style={{ cursor: 'pointer' }}>
                Automatisch abgerufener LinkedIn-Post ansehen
                {enrichment.signalUrl && (
                  <>
                    {' '}
                    (<a href={enrichment.signalUrl} target="_blank" rel="noreferrer">Link</a>)
                  </>
                )}
              </summary>
              <MarkdownView content={enrichment.signalText} />
            </details>
          )}
        </div>
      )}

      {/* Ergebnisse */}
      {variants.length > 0 && (
        <>
          {contextUsed.length > 0 && (
            <p style={{ color: 'var(--sec-fg-muted)', fontSize: '13px', marginTop: '20px' }}>
              Personalisiert auf Basis von: <strong>{contextUsed.join(', ')}</strong>
            </p>
          )}
          {[
            { title: 'E-Mail', list: emailVariants },
            { title: 'LinkedIn', list: linkedinVariants },
          ]
            .filter((g) => g.list.length > 0)
            .map((group) => (
              <div key={group.title} style={{ marginTop: '12px' }}>
                <h3>{group.title}</h3>
                {group.list.map((v) => {
                  const key = `${v.channel}-${v.label}`;
                  return (
                    <div key={key} className="card" style={{ marginBottom: '12px' }}>
                      <div className="card-header">
                        <div className="card-title">
                          Variante {v.label}
                          {v.angle && <span style={{ color: 'var(--sec-fg-muted)', fontWeight: 400 }}> — {v.angle}</span>}
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={() => copy(key, v.fullMessage)}>
                          {copied === key ? 'Kopiert!' : 'Nachricht kopieren'}
                        </button>
                      </div>
                      {v.subject && (
                        <p style={{ margin: '8px 0' }}>
                          <strong>Betreff:</strong> {v.subject}
                        </p>
                      )}
                      <div style={{ margin: '8px 0' }}>
                        <span style={{ color: 'var(--sec-coral-600)', fontSize: '12px', fontWeight: 600 }}>ICEBREAKER (Einleitung)</span>
                        <MarkdownView content={v.icebreaker} style={{ marginTop: '4px' }} />
                      </div>
                      <div style={{ margin: '8px 0' }}>
                        <span style={{ color: 'var(--sec-fg-muted)', fontSize: '12px', fontWeight: 600 }}>VOLLSTÄNDIGER ENTWURF</span>
                        <MarkdownView content={v.fullMessage} style={{ marginTop: '4px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
        </>
      )}

      <SavedAiDocuments feature="icebreaker" reloadKey={reloadKey} />
    </div>
  );
};

export default IcebreakerPage;
